import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Pop } from '../../features/nudges/Pop';
import { useNudges } from '../../features/nudges/NudgeProvider';
import api from '../../services/api';
import { STUDENT_SUBMIT_PARENT_EMAIL, USER_PROFILE } from '../../config/Config';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Stack,
  TextField,
  CircularProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Person as PersonIcon,
  VpnKey as VpnKeyIcon,
  ExitToApp,
  Help as HelpIcon,
  Menu as MenuIcon
} from '@mui/icons-material';
import StudentNavigation from './StudentNavigation';
import SupportDialog from '../SupportDialog';
import {
  handleProfile,
  handleChangePassword,
  handleLogout
} from '../common/GeneralnNagivationHandler';

const StudentLayout = ({ children, onHelpClick }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, setUser } = useAuth();
  const { nudges, loading, error } = useNudges();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showPop, setShowPop] = useState(false);
  const [shownNudges, setShownNudges] = useState(new Set());
  const [helpOpen, setHelpOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [consentDialogOpen, setConsentDialogOpen] = useState(false);
  const [consentEmail, setConsentEmail] = useState('');
  const [consentError, setConsentError] = useState('');
  const [consentSuccess, setConsentSuccess] = useState('');
  const [submittingConsent, setSubmittingConsent] = useState(false);

  const parentConsentRestricted = !!user?.requires_parent_consent;

  useEffect(() => {
    setConsentEmail(user?.parent_email || '');
  }, [user?.parent_email]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleToggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  const handleProfileClick = () => {
    handleProfile(navigate, () => setAnchorEl(null));
    setAnchorEl(null);
  };

  const handleChangePasswordClick = () => {
    handleChangePassword(navigate, () => setAnchorEl(null));
    setAnchorEl(null);
  };

  const handleLogoutClick = () => {
    handleLogout(logout, () => setMobileOpen(false), navigate);
    setAnchorEl(null);
  };

  const handleHelpClick = () => {
    setAnchorEl(null); // close account menu if open
    if (onHelpClick) {
      onHelpClick();
    } else {
      setHelpOpen(true);
    }
  };

  const handleCloseHelp = () => {
    setHelpOpen(false);
  };

  const handleOpenSupport = () => {
    setHelpOpen(false);
    setSupportOpen(true);
  };

  const refreshProfile = async () => {
    const profileRes = await api.get(USER_PROFILE);
    if (setUser) {
      setUser(profileRes.data);
    }
    if (localStorage.getItem('token')) {
      localStorage.setItem('user', JSON.stringify(profileRes.data));
    }
    if (sessionStorage.getItem('token')) {
      sessionStorage.setItem('user', JSON.stringify(profileRes.data));
    }
    return profileRes.data;
  };

  const openConsentDialog = () => {
    setConsentError('');
    setConsentSuccess('');
    setConsentEmail(user?.parent_email || '');
    setConsentDialogOpen(true);
  };

  const handleSubmitConsent = async () => {
    const nextEmail = consentEmail.trim().toLowerCase();

    if (!nextEmail) {
      setConsentError('Please enter the parent or guardian email you want to use.');
      return;
    }

    setSubmittingConsent(true);
    setConsentError('');
    setConsentSuccess('');
    try {
      const res = await api.post(STUDENT_SUBMIT_PARENT_EMAIL, {
        parent_email: nextEmail,
        is_same_as_student: false,
      });
      await refreshProfile();
      setConsentSuccess(
        res.data?.message || 'We sent a fresh parent consent email. Any earlier consent link has been deactivated.'
      );
    } catch (err) {
      setConsentError(err?.response?.data?.error || 'We could not send the parent consent email right now.');
    } finally {
      setSubmittingConsent(false);
    }
  };

  const stackedMessages = [];

  if (parentConsentRestricted) {
    stackedMessages.push({
      key: 'parent-consent',
      severity: 'warning',
      title: 'Parent consent is still pending.',
      description: user?.parent_email
        ? `Until consent is approved, only limited access is available. The current consent email is ${user.parent_email}.`
        : 'Until consent is approved, only limited access is available. Add or update the parent or guardian email to continue.',
    });
  }

  if (location.state?.message) {
    stackedMessages.push({
      key: 'location-message',
      severity: 'info',
      title: location.state.message,
      description: '',
    });
  }

  // Show pop when there are nudges that haven't been shown yet
  useEffect(() => {
    if (nudges && nudges.length > 0) {
      // Check if there are any nudges that haven't been shown yet
      const unshownNudges = nudges.filter(nudge => nudge && nudge.id && !shownNudges.has(nudge.id));
      
      if (unshownNudges.length > 0) {
        setShowPop(true);
      }
    }
  }, [nudges, shownNudges]);

  // Handle popup close - mark nudges as shown
  const handlePopClose = () => {
    if (nudges && nudges.length > 0) {
      // Add all current nudges to shown set
      const newShownNudges = new Set(shownNudges);
      nudges.forEach(nudge => {
        if (nudge && nudge.id) {
          newShownNudges.add(nudge.id);
        }
      });
      setShownNudges(newShownNudges);
    }
    setShowPop(false);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Navigation Drawer */}
      <StudentNavigation
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        collapsed={collapsed}
        onToggleCollapse={handleToggleCollapse}
        restricted={parentConsentRestricted}
      />

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minHeight: '100vh',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          overflow: 'hidden'
        }}
      >
        {/* Top Bar */}
        <AppBar
          position="static"
          elevation={0}
          sx={{
            width: '100%',
            backgroundColor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
            color: 'text.primary'
          }}
        >
          <Toolbar
            sx={{
              minHeight: { xs: 56, sm: 64 },
              px: { xs: 1, sm: 2 },
              pr: { xs: 1, sm: 2 },
              overflow: 'hidden',
              width: '100%',
              boxSizing: 'border-box'
            }}
          >
            {/* Mobile Menu Button */}
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 1, flexShrink: 0 }}
              >
                <MenuIcon />
              </IconButton>
            )}

            {/* Spacer */}
            <Box sx={{ flexGrow: 1, minWidth: 0 }} />

            {/* Right side: on mobile only Avatar (Help is inside menu); on desktop Help + Avatar */}
            <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, gap: 0 }}>
              {!isMobile && (
                <Button
                  startIcon={<HelpIcon />}
                  onClick={handleHelpClick}
                  sx={{
                    mr: 1,
                    color: 'text.secondary',
                    '&:hover': { backgroundColor: 'action.hover' }
                  }}
                >
                  Need Help
                </Button>
              )}

              {/* User Avatar - single right-side control on mobile so it stays visible */}
              <IconButton
                onClick={(event) => setAnchorEl(event.currentTarget)}
                aria-label="Account and help menu"
                sx={{ p: 0.5 }}
              >
                <Avatar
                  sx={{
                    width: { xs: 36, sm: 40 },
                    height: { xs: 36, sm: 40 },
                    bgcolor: 'primary.main',
                    color: 'white',
                    fontSize: { xs: '0.85rem', sm: '1rem' },
                    fontWeight: 600
                  }}
                >
                  {user?.first_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                </Avatar>
              </IconButton>
            </Box>

            {/* Account menu - on mobile includes "Need Help" as first item */}
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              PaperProps={{
                elevation: 3,
                sx: {
                  minWidth: 220,
                  mt: 1.5,
                  maxHeight: 'calc(100vh - 100px)'
                }
              }}
            >
              {isMobile && (
                <>
                  <MenuItem onClick={handleHelpClick}>
                    <ListItemIcon>
                      <HelpIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Need Help</ListItemText>
                  </MenuItem>
                  <Divider />
                </>
              )}
              <MenuItem disabled sx={{ opacity: 0.7 }}>
                <ListItemText
                  primary={user?.email || 'No email'}
                  primaryTypographyProps={{
                    variant: 'body2',
                    color: 'text.secondary',
                    sx: { fontSize: '0.875rem' }
                  }}
                />
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleProfileClick}>
                <ListItemIcon>
                  <PersonIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Profile</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleChangePasswordClick}>
                <ListItemIcon>
                  <VpnKeyIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Change Password</ListItemText>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogoutClick}>
                <ListItemIcon>
                  <ExitToApp fontSize="small" />
                </ListItemIcon>
                <ListItemText>Logout</ListItemText>
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        {/* Page Content - responsive padding for mobile */}
        <Box
          sx={{
            flex: 1,
            p: { xs: 1.5, sm: 2, md: 3 },
            backgroundColor: 'background.default',
            minHeight: 'calc(100vh - 64px)',
            overflowX: 'hidden'
          }}
        >
          {stackedMessages.length > 0 ? (
            <Stack spacing={1.5} sx={{ mb: 2.5 }}>
              {stackedMessages.map((message) => (
                <Alert
                  key={message.key}
                  severity={message.severity}
                  action={
                    message.key === 'parent-consent' ? (
                      <Button color="inherit" size="small" onClick={openConsentDialog}>
                        Reinitiate Consent
                      </Button>
                    ) : undefined
                  }
                  sx={{ alignItems: 'center' }}
                >
                  <Box>
                    <Box sx={{ fontWeight: 700, mb: message.description ? 0.4 : 0 }}>{message.title}</Box>
                    {message.description ? <Box>{message.description}</Box> : null}
                  </Box>
                </Alert>
              ))}
            </Stack>
          ) : null}
          {children}
        </Box>
      </Box>

      {/* Pop Component for Nudges */}
      <Pop 
        open={showPop} 
        onClose={handlePopClose} 
      />

      {/* Help dialog when no onHelpClick provided - mobile-friendly typography and layout */}
      <Dialog
        open={helpOpen}
        onClose={handleCloseHelp}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            m: { xs: 2, sm: 3 },
            maxWidth: { xs: 'calc(100% - 32px)', sm: 440 },
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: { xs: '1.1rem', sm: '1.25rem' },
            fontWeight: 600,
            px: { xs: 2, sm: 3 },
            pt: { xs: 2, sm: 2.5 },
            pb: 0,
          }}
        >
          Need Help?
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 }, pt: 1.5, pb: 0 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontSize: { xs: '0.875rem', sm: '0.875rem' },
              lineHeight: 1.5,
              mb: 1.5,
            }}
          >
            If you have questions about tests, assignments, or your account, we&apos;re here to help.
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontSize: { xs: '0.875rem', sm: '0.875rem' },
              lineHeight: 1.5,
            }}
          >
            Use the button below to send a support request and we&apos;ll get back to you as soon as possible.
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            py: { xs: 2, sm: 2 },
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            gap: 1,
            '& .MuiButton-root': {
              minHeight: 44,
              minWidth: { xs: '100%', sm: 'auto' },
            },
          }}
        >
          <Button onClick={handleCloseHelp} variant="outlined" fullWidth={isMobile}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleOpenSupport}
            startIcon={<HelpIcon />}
            fullWidth={isMobile}
          >
            Contact Support
          </Button>
        </DialogActions>
      </Dialog>

      {/* Support request dialog */}
      <SupportDialog
        open={supportOpen}
        onClose={() => setSupportOpen(false)}
        presetEmail={user?.email || ''}
      />

      <Dialog
        open={consentDialogOpen}
        onClose={() => {
          if (!submittingConsent) {
            setConsentDialogOpen(false);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reinitiate Parent Consent</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {consentError ? <Alert severity="error">{consentError}</Alert> : null}
            {consentSuccess ? <Alert severity="success">{consentSuccess}</Alert> : null}

            <TextField
              fullWidth
              label="Previous consent email"
              value={consentEmail}
              onChange={(e) => setConsentEmail(e.target.value)}
              type="email"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setConsentDialogOpen(false)} disabled={submittingConsent}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitConsent}
            disabled={submittingConsent}
            startIcon={submittingConsent ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {submittingConsent ? 'Resending...' : 'Resend'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StudentLayout; 
